import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { useStytchMemberSession } from '@stytch/react/b2b';
import { useStytchB2BClient } from '@stytch/react/b2b';
import { useEffect, useState, type ReactNode } from "react";

/*
During both the Magic link and OAuth flow, Stytch will redirect the user back to your application to a specified redirect URL (see Login.js).
Stytch will append query parameters to the redirect URL which are then used to complete the authentication flow.
A redirect URL for this example app will look something like: http://localhost:3000/?stytch_token_type=multi_tenant_magic_links&token=abc123

TokenAuthenticator will detect the presence of a token in the query parameters, and attempt to authenticate it.
On successful authentication, a session will be created and the user will be shown Profile.js
*/
interface TokenAuthenticatorProps {
    children?: ReactNode;
}

// This is a B2B (multi-tenant) Stytch project, so its magic links arrive as `multi_tenant_magic_links`.
// The plain `magic_links` type belongs to consumer projects and is never emitted here — matching on it meant
// the "log in without password" link in the reset-password email was silently ignored and the user just
// landed on the sign-in page. The SDK's own supported list is:
//   discovery, discovery_oauth, oauth, sso, multi_tenant_magic_links, multi_tenant_impersonation
const MAGIC_LINK_TOKEN_TYPE = "multi_tenant_magic_links";

// Routes that redeem their own token and must be left alone.
//
// This component sits ABOVE the router, so it runs on every path. The invite email is sent with
// MagicLinks.Email.Invite and points at /invite, which means it arrives as multi_tenant_magic_links — the
// same type handled below. Once that type started being handled, this redeemed the invite token first,
// created a session and redirected away, so an invited user was silently logged straight in and never got
// the chance to set a password (TTD-135). /resetpassword owns its token the same way.
//
// Matching on the path rather than the token type because the type is genuinely the same; only the
// destination says who the token belongs to.
const TOKEN_OWNING_PATHS = ["/invite", "/resetpassword"];

// Where to send someone whose link failed. A spent link must not drop them on the organisation chooser,
// which is an internal screen no ordinary user should be shown.
//
// Stytch puts the organisation slug on the redirect it builds, so the link itself says which org it belongs
// to — including on a browser that has never signed in here, which is the case the remembered value cannot
// cover. QA spotted that; it is read from the URL first for exactly that reason.
const ORG_SLUG_PARAM = "slug";

// Fallback for a link that arrives without the parameter. Remembered when the reset email is requested (see
// Login.tsx) under a key of its own, because AppContext's copy is cleared on sign-out.
const LAST_ORG_SLUG_KEY = "touchtab-portal-last-org-slug";

// A Stytch token is single-use, and StrictMode runs every effect twice in a development build — which the Dev
// deployment is (`vite build --mode development`). Without this the second attempt redeems an already-spent
// token, fails, and races its own failure handler against the successful redirect. Module scope on purpose:
// a component-level ref is recreated by the same remount StrictMode is simulating.
const attemptedTokens = new Set<string>();

// True when this component is the one that should redeem whatever is in the URL. Computed from the URL rather
// than from state so the first render already knows, which is what lets the app be held back below.
const hasTokenToRedeem = (): boolean => {
    if (TOKEN_OWNING_PATHS.includes(window.location.pathname)) return false;

    const queryParams = new URLSearchParams(window.location.search);
    return !!queryParams.get("token") && !!queryParams.get("stytch_token_type");
};

const TokenAuthenticator = ({ children }: TokenAuthenticatorProps): ReactNode => {
    const stytch = useStytchB2BClient();
    const { session } = useStytchMemberSession();
    // Held back while a token is being redeemed. Without this the app renders underneath at "/", which is the
    // organisation chooser, so a passwordless sign-in flashed an internal screen on its way through.
    const [redeeming] = useState(hasTokenToRedeem);

    useEffect(() => {
        // If the stytch SDK is available, and there is no existing user check for a token value in query params
        if (!stytch || session) return;

        // The token belongs to the page being loaded, which redeems it itself.
        if (TOKEN_OWNING_PATHS.includes(window.location.pathname)) return;

        const queryParams = new URLSearchParams(window.location.search);
        const token = queryParams.get("token");
        const tokenType = queryParams.get("stytch_token_type");

        if (!token || !tokenType) return;
        if (attemptedTokens.has(token)) return;
        attemptedTokens.add(token);

        const sessionDuration = parseInt(import.meta.env.VITE_STYTCH_SESSION_DURATION);

        // A full reload rather than a router navigate: this component sits above AppProvider and the router in
        // main.tsx, so it has neither. Reloading also lets the rest of the app re-read the now-authenticated
        // Stytch session from scratch, which is how the org slug gets seeded.
        const goHome = (orgSlug?: string) => {
            window.location.href = orgSlug ? `/${orgSlug}` : "/";
        };

        // Failures used to be swallowed entirely, so an expired or already-used link left the user staring at
        // the sign-in page with no clue why.
        //
        // The spent token is stripped, and the user goes to their organisation's sign-in page rather than
        // being left at "/" — which is the organisation chooser, an internal screen. The org is only known
        // here because the reset email request remembered it.
        const handleFailure = (err: unknown) => {
            console.error("Stytch token authentication failed:", err);
            // The link's own slug first: it is the only source that works on a browser which has never
            // signed in here. The remembered value covers a link that arrives without the parameter.
            const orgSlug = queryParams.get(ORG_SLUG_PARAM) || localStorage.getItem(LAST_ORG_SLUG_KEY);
            if (orgSlug) {
                window.location.href = `/${orgSlug}`;
                return;
            }

            queryParams.delete("token");
            queryParams.delete("stytch_token_type");
            const query = queryParams.toString();
            window.location.href = `${window.location.pathname}${query ? `?${query}` : ""}`;
        };

        // If a token is found, authenticate it with the appropriate method
        if (tokenType === MAGIC_LINK_TOKEN_TYPE) {
            stytch.magicLinks
                .authenticate({
                    magic_links_token: token,
                    session_duration_minutes: sessionDuration,
                })
                .then((response) => goHome(response.organization?.organization_slug))
                .catch(handleFailure);
        }
        else if (tokenType === "oauth") {
            stytch.oauth
                .authenticate({
                    oauth_token: token,
                    session_duration_minutes: sessionDuration,
                })
                .then((response) => goHome(response.organization?.organization_slug))
                .catch(handleFailure);
        }
    }, [stytch, session]);

    // Deliberately bare, and deliberately not the app: every path out of the effect above is a full page
    // navigation, so this is only ever on screen in transit.
    if (redeeming && !session) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100dvh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return children ?? null;
};

export default TokenAuthenticator;
